/**
 * Image popup.
 *
 * Shows a single clickable promo image in a centered modal, triggered by
 * EITHER a time delay OR a scroll percentage (whichever the merchant picked
 * in the theme editor — never both at once). Clicking the image navigates
 * to the configured link; clicking the close button or the overlay just
 * dismisses it.
 *
 * "Once per session" uses sessionStorage — it clears itself when the
 * browser tab/window closes, so the popup can show again on the customer's
 * next visit, but won't repeat while they're browsing around the same site
 * in one sitting.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initInstance(root) {
    if (!root || root.dataset.imagePopupInit === 'true') return;
    root.dataset.imagePopupInit = 'true';

    var sessionKey = root.getAttribute('data-session-key');
    var oncePerSession = root.getAttribute('data-once-per-session') === 'true';

    if (oncePerSession) {
      try {
        if (window.sessionStorage.getItem(sessionKey) === 'true') return;
      } catch (e) {
        /* sessionStorage unavailable (privacy mode, etc.) — fall through
           and just show it; not worth blocking the popup over this. */
      }
    }

    var overlay = root.querySelector('[data-image-popup-overlay]');
    var closeBtn = root.querySelector('[data-image-popup-close]');
    var link = root.querySelector('[data-image-popup-link]');
    var lastFocusedBeforeOpen = null;

    function markShown() {
      if (!oncePerSession) return;
      try {
        window.sessionStorage.setItem(sessionKey, 'true');
      } catch (e) {
        /* Ignore — worst case it can show again this session. */
      }
    }

    function trapFocus(evt) {
      if (evt.key !== 'Tab') return;
      var focusable = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocusedBeforeOpen = document.activeElement;
      document.addEventListener('keydown', trapFocus, true);
      root.hidden = false;
      void root.offsetWidth; // force reflow so the entrance animation replays
      root.classList.add('image-popup--visible');
      document.documentElement.classList.add('image-popup-lock-scroll');
      markShown();
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      root.classList.remove('image-popup--visible');
      document.documentElement.classList.remove('image-popup-lock-scroll');
      document.removeEventListener('keydown', trapFocus, true);
      window.setTimeout(function () {
        if (!root.classList.contains('image-popup--visible')) root.hidden = true;
      }, 300);
      if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
        lastFocusedBeforeOpen.focus();
      }
      lastFocusedBeforeOpen = null;
    }

    if (overlay) overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    // The link itself navigates on click via its own href — no extra JS
    // needed there. Escape still closes without navigating.
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && root.classList.contains('image-popup--visible')) {
        close();
      }
    });

    var triggerMode = root.getAttribute('data-trigger');

    if (triggerMode === 'scroll') {
      var scrollPercent = parseFloat(root.getAttribute('data-scroll-percent')) || 50;
      var fired = false;
      var onScroll = function () {
        if (fired) return;
        var doc = document.documentElement;
        var scrollable = doc.scrollHeight - doc.clientHeight;
        var scrolledPct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
        if (scrolledPct >= scrollPercent) {
          fired = true;
          window.removeEventListener('scroll', onScroll);
          open();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      // Covers a page short enough that it's never actually scrollable —
      // treat that as "already at 100% scrolled" so the popup can still
      // fire rather than waiting forever for a scroll event that can't
      // reach the threshold.
      onScroll();
    } else {
      var delaySeconds = parseFloat(root.getAttribute('data-delay-seconds')) || 5;
      window.setTimeout(open, delaySeconds * 1000);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-image-popup]').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var section = evt.target.querySelector('[data-image-popup]');
    if (section) {
      section.dataset.imagePopupInit = 'false';
      initInstance(section);
    }
  });
})();